import { ApolloProvider } from '@apollo/client';
import { ThemeProvider } from '@emotion/react';
import { Router } from '@reach/router';
import * as Sentry from '@sentry/react';
import { lazy, Suspense, useEffect } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { usePageVisibility } from 'react-page-visibility';

import { LoadingProvider } from 'components/LoadingProvider';
import SessionTimeoutDialog from 'components/SessionTimeoutDialog';
import { AlertProvider } from 'utils/alert-context';
import Background from './components/Background';

import './global.css';
import theme from './theme';

import GlobalHelmet from './GlobalHelmet';
import createApolloClient from './graphql/client';

import Theme from './pages/Theme/Theme';
import ErrorPage from './pages/ErrorPage';
import { getTenant } from './pages/Login/sso-utils';
import NotFound from './pages/NotFound';
import ProtectedRoute from './pages/ProtectedRoute';
import Tournament from './pages/Lobby/Tournament/Tournament';
const LobbyGame = lazy(() => import('./pages/Lobby/LobbyGame/LobbyGame'));
const FreeGame = lazy(() => import('./pages/Lobby/FreeGame/FreeGame'));
const Training = lazy(() => import('./pages/Lobby/Training/Training'));
const UnfinishedGames = lazy(
  () => import('./pages/Lobby/UnfinishedGames/UnfinishedGames'),
);
const Ranking = lazy(() => import('./pages/Lobby/Ranking/Ranking'));
const Game = lazy(() => import('./pages/Game'));
const FirstTimeView = lazy(() => import('./pages/FirstTimeView'));
const Login = lazy(() => import('./pages/Login/Login'));
const LoginChangeLayoutPage = lazy(
  () => import('./pages/Login/LoginChangeLayoutPage'),
);
const SSOLogin = lazy(() => import('./pages/Login/SSOLogin'));
const TwoFactorAuthentication = lazy(
  () => import('./pages/Login/TwoFactorAuthentication'),
);
const SSOLoginCallback = lazy(() => import('./pages/Login/SSOLoginCallback'));
const Logout = lazy(() => import('./pages/Login/Logout'));
const ResetPassword = lazy(() => import('./pages/Login/ResetPassword'));
const MagicLink = lazy(() => import('./pages/Login/MagicLink'));
const AcceptInvitation = lazy(() => import('./pages/Login/AcceptInvitation'));
const Certificate = lazy(() => import('./pages/Certificate/Certificate'));

const client = createApolloClient();
const App = () => {
  const isVisible = usePageVisibility();

  useEffect(() => {
    if (isVisible) Sentry.addBreadcrumb({ message: 'Page is visible.' });
    else Sentry.addBreadcrumb({ message: 'Page is not visible.' });
  }, [isVisible]);

  const tenant = getTenant();

  const adjustedTheme = () => {
    if (process.env.NODE_ENV === 'development') {
      return theme;
    }

    return {
      ...theme,
      layout: {
        ...theme.layout,
        edu: window.location.origin.indexOf('edu') > -1,
      },
    };
  };

  const { layout } = adjustedTheme();
  const IS_EDU = layout?.edu;

  return (
    <ApolloProvider client={client}>
      <ThemeProvider theme={adjustedTheme}>
        <HelmetProvider>
          <Sentry.ErrorBoundary
            fallback={({ eventId, resetError }) => (
              <ErrorPage eventId={eventId} onRetry={resetError} />
            )}
          >
            <AlertProvider>
              <Background />
              <Suspense fallback={<div />}>
                <GlobalHelmet />
                <LoadingProvider>
                  <Router>
                    {tenant == null ? (
                      <>
                        <Login path="/login" />
                        <LoginChangeLayoutPage path="/login-layout/:ssoData" />
                        <TwoFactorAuthentication path="/login/twoFactorAuthentication" />
                        <ResetPassword path="/password-reset" />
                        <MagicLink path="/magic-link" />
                        <AcceptInvitation path="/accept-invitation/:token" />
                      </>
                    ) : (
                      <>
                        <SSOLogin path="/login" />
                      </>
                    )}
                    <Logout path="/logout" />
                    <SSOLoginCallback path="/login/callback" />
                    <SSOLoginCallback path="/login/:ssoConfig/callback" />
                    {IS_EDU ? (
                      <ProtectedRoute component={LobbyGame} path="/" />
                    ) : (
                      <ProtectedRoute component={FreeGame} path="/" />
                    )}
                    <ProtectedRoute component={Tournament} path="/tournament" />
                    <ProtectedRoute component={Training} path="/training" />
                    <ProtectedRoute component={Training} path="/training/:trainingId" />
                    <ProtectedRoute
                      component={UnfinishedGames}
                      path="/unfinished-games"
                    />
                    <ProtectedRoute component={Ranking} path="/ranking" />
                    <ProtectedRoute component={Game} path="game/:gameId" />
                    <ProtectedRoute
                      component={FirstTimeView}
                      path="/first-time-view"
                    />
                    <ProtectedRoute
                      component={Certificate}
                      path="/certificate/:trainingId"
                    />
                    <ProtectedRoute component={Theme} path="/theme" />
                    <NotFound default />
                  </Router>
                </LoadingProvider>
                <SessionTimeoutDialog />
              </Suspense>
            </AlertProvider>
          </Sentry.ErrorBoundary>
        </HelmetProvider>
      </ThemeProvider>
    </ApolloProvider>
  );
};

export default App;
